import { api, type ConfigCheckItem } from '../api/client';
import { Async } from '../components/Async';
import { useFetch } from '../useFetch';

/**
 * What is configured, and what silently does not work without it.
 *
 * Built after two settings were missed in two days — one because it had to be applied to two
 * separate Workers, one because it took three manual steps. Both times the platform kept running
 * and said nothing, because an unset value looks exactly like a feature nobody has used yet.
 *
 * Rows are ordered by how much they should worry you, and every row says what BREAKS rather than
 * just what is absent. A list of names is a chore; a list of consequences is a decision.
 */
export function ConfigCheck() {
  const state = useFetch(() => api.configCheck());

  return (
    <section>
      <h3>Configuration</h3>
      <Async state={state}>
        {(data) => (
          <>
            <p className="muted">
              {data.summary.requiredMissing > 0
                ? `${data.summary.requiredMissing} required setting${data.summary.requiredMissing === 1 ? '' : 's'} missing.`
                : 'Everything required is set.'}
              {data.summary.recommendedMissing > 0 &&
                ` ${data.summary.recommendedMissing} ${
                  data.summary.recommendedMissing === 1 ? 'capability' : 'capabilities'
                } quietly off.`}
              {data.summary.unknown > 0 && ` ${data.summary.unknown} could not be checked.`}
            </p>
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Setting</th>
                  <th>Effect if missing</th>
                </tr>
              </thead>
              <tbody>
                {[...data.checks].sort(byConcern).map((check) => (
                  <tr key={`${check.where}:${check.name}`}>
                    <td>
                      <span className={`sev sev-${severityClass(check)}`}>{label(check)}</span>
                    </td>
                    <td>
                      {check.name}
                      <span className="muted"> · {check.where}</span>
                    </td>
                    <td className="muted">{check.status === 'ok' ? '—' : check.consequence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Async>
    </section>
  );
}

/** Worst first: broken, silently off, unknown, healthy, and finally deliberately unused. */
function byConcern(a: ConfigCheckItem, b: ConfigCheckItem): number {
  const rank = (c: ConfigCheckItem) =>
    c.status === 'missing' && c.severity === 'required'
      ? 0
      : c.status === 'missing'
        ? 1
        : c.status === 'unknown'
          ? 2
          : c.status === 'ok'
            ? 3
            : 4;
  return rank(a) - rank(b);
}

const label = (c: ConfigCheckItem) =>
  c.status === 'ok'
    ? 'set'
    : c.status === 'unknown'
      ? 'unchecked'
      : c.status === 'not_applicable'
        ? 'not needed'
        : 'missing';

/** `unknown` deliberately does not read as an error — it is an honest gap, not a fault. */
const severityClass = (c: ConfigCheckItem) =>
  c.status === 'ok'
    ? 'info'
    : c.status === 'unknown' || c.status === 'not_applicable'
      ? 'info'
      : c.severity === 'required'
        ? 'critical'
        : 'warn';
